import { Editor } from '../core/Editor';
import { Adjustments } from '../filters/Adjustments';
import { ImageAdjustmentValues } from '../types';

export class AdjustmentsModal {
  private editor: Editor;
  private container: HTMLDivElement | null = null;
  private originalSnapshot: ImageData | null = null;

  private values: ImageAdjustmentValues = {
    brightness: 0,
    contrast: 0,
    saturation: 0,
    hue: 0,
    exposure: 0,
    temperature: 0,
    blur: 0,
    sharpen: 0,
    invert: false,
    grayscale: false,
    sepia: false
  };

  constructor(editor: Editor) {
    this.editor = editor;
  }

  public open() {
    const layer = this.editor.getActiveLayer();
    if (!layer) {
      this.editor.showToast('Please select a layer first');
      return;
    }

    // Save snapshot of layer to revert or preview
    this.originalSnapshot = layer.ctx.getImageData(0, 0, layer.width, layer.height);
    this.resetValues();
    this.createModal();
  }

  public close(revert: boolean = false) {
    if (revert && this.originalSnapshot) {
      const layer = this.editor.getActiveLayer();
      if (layer) {
        layer.ctx.putImageData(this.originalSnapshot, 0, 0);
        this.editor.render();
      }
    }
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  private resetValues() {
    this.values = {
      brightness: 0,
      contrast: 0,
      saturation: 0,
      hue: 0,
      exposure: 0,
      temperature: 0,
      blur: 0,
      sharpen: 0,
      invert: false,
      grayscale: false,
      sepia: false
    };
  }

  private createModal() {
    this.close(false);

    const overlay = document.createElement('div');
    overlay.className = 'modal-backdrop';
    overlay.id = 'adjustments-modal';

    overlay.innerHTML = `
      <div class="modal-card adjustments-card">
        <div class="modal-header">
          <div class="modal-title-group">
            <div class="modal-icon">🎨</div>
            <div>
              <h3>Image Adjustments & Color</h3>
              <p class="modal-subtitle">Fine-tune exposure, contrast, color temperature, and texture</p>
            </div>
          </div>
          <button class="modal-close-btn" id="close-adjust">&times;</button>
        </div>

        <div class="adjust-modal-body">
          <div class="presets-row">
            <span class="preset-label">Presets:</span>
            <button type="button" class="preset-pill" data-preset="vivid">Vivid Color</button>
            <button type="button" class="preset-pill" data-preset="warm">Golden Hour</button>
            <button type="button" class="preset-pill" data-preset="cool">Cool Cyber</button>
            <button type="button" class="preset-pill" data-preset="dramatic-bw">Dramatic B&W</button>
            <button type="button" class="preset-pill" data-preset="vintage">Vintage Film</button>
            <button type="button" class="preset-pill" data-preset="reset">Reset</button>
          </div>

          <div class="sliders-grid">
            <!-- Brightness -->
            <div class="control-group">
              <div class="label-with-value">
                <label>Brightness</label>
                <span class="value-badge" id="val-bright">0</span>
              </div>
              <input type="range" id="adj-bright" min="-100" max="100" value="0" class="styled-range" />
            </div>

            <!-- Contrast -->
            <div class="control-group">
              <div class="label-with-value">
                <label>Contrast</label>
                <span class="value-badge" id="val-contrast">0</span>
              </div>
              <input type="range" id="adj-contrast" min="-100" max="100" value="0" class="styled-range" />
            </div>

            <!-- Exposure -->
            <div class="control-group">
              <div class="label-with-value">
                <label>Exposure</label>
                <span class="value-badge" id="val-exp">0</span>
              </div>
              <input type="range" id="adj-exp" min="-100" max="100" value="0" class="styled-range" />
            </div>

            <!-- Saturation -->
            <div class="control-group">
              <div class="label-with-value">
                <label>Saturation</label>
                <span class="value-badge" id="val-sat">0</span>
              </div>
              <input type="range" id="adj-sat" min="-100" max="100" value="0" class="styled-range" />
            </div>

            <!-- Temperature -->
            <div class="control-group">
              <div class="label-with-value">
                <label>Temperature (Warm/Cool)</label>
                <span class="value-badge" id="val-temp">0</span>
              </div>
              <input type="range" id="adj-temp" min="-100" max="100" value="0" class="styled-range" />
            </div>

            <!-- Blur -->
            <div class="control-group">
              <div class="label-with-value">
                <label>Gaussian Blur</label>
                <span class="value-badge" id="val-blur">0px</span>
              </div>
              <input type="range" id="adj-blur" min="0" max="15" value="0" class="styled-range" />
            </div>

            <!-- Sharpen -->
            <div class="control-group">
              <div class="label-with-value">
                <label>Sharpen</label>
                <span class="value-badge" id="val-sharp">0%</span>
              </div>
              <input type="range" id="adj-sharp" min="0" max="100" value="0" class="styled-range" />
            </div>
          </div>

          <div class="toggles-row">
            <label class="checkbox-label">
              <input type="checkbox" id="chk-bw" />
              <span>Black & White</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" id="chk-sepia" />
              <span>Sepia Tone</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" id="chk-invert" />
              <span>Invert Colors</span>
            </label>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn btn-secondary" id="cancel-adj">Cancel</button>
          <button class="btn btn-primary" id="apply-adj">Apply Adjustments</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.container = overlay;

    // Attach listeners
    overlay.querySelector('#close-adjust')?.addEventListener('click', () => this.close(true));
    overlay.querySelector('#cancel-adj')?.addEventListener('click', () => this.close(true));
    overlay.querySelector('#apply-adj')?.addEventListener('click', () => {
      this.editor.recordHistory('Image Adjustments');
      this.close(false);
    });

    const bindSlider = (id: string, valId: string, prop: keyof ImageAdjustmentValues, unit: string = '') => {
      const slider = overlay.querySelector(`#${id}`) as HTMLInputElement;
      const display = overlay.querySelector(`#${valId}`) as HTMLElement;
      slider?.addEventListener('input', () => {
        const val = parseInt(slider.value);
        if (display) display.textContent = `${val}${unit}`;
        (this.values as any)[prop] = val;
        this.renderLive();
      });
    };

    bindSlider('adj-bright', 'val-bright', 'brightness');
    bindSlider('adj-contrast', 'val-contrast', 'contrast');
    bindSlider('adj-exp', 'val-exp', 'exposure');
    bindSlider('adj-sat', 'val-sat', 'saturation');
    bindSlider('adj-temp', 'val-temp', 'temperature');
    bindSlider('adj-blur', 'val-blur', 'blur', 'px');
    bindSlider('adj-sharp', 'val-sharp', 'sharpen', '%');

    // Checkboxes
    const chkBw = overlay.querySelector('#chk-bw') as HTMLInputElement;
    chkBw?.addEventListener('change', () => {
      this.values.grayscale = chkBw.checked;
      this.renderLive();
    });

    const chkSepia = overlay.querySelector('#chk-sepia') as HTMLInputElement;
    chkSepia?.addEventListener('change', () => {
      this.values.sepia = chkSepia.checked;
      this.renderLive();
    });

    const chkInvert = overlay.querySelector('#chk-invert') as HTMLInputElement;
    chkInvert?.addEventListener('change', () => {
      this.values.invert = chkInvert.checked;
      this.renderLive();
    });

    // Presets
    overlay.querySelectorAll('.preset-pill').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const preset = (e.currentTarget as HTMLElement).dataset.preset;
        this.applyPreset(preset || '');
      });
    });
  }

  private applyPreset(preset: string) {
    if (!this.container) return;
    this.resetValues();

    if (preset === 'vivid') {
      this.values.saturation = 35;
      this.values.contrast = 20;
      this.values.sharpen = 25;
    } else if (preset === 'warm') {
      this.values.temperature = 40;
      this.values.saturation = 15;
      this.values.brightness = 5;
    } else if (preset === 'cool') {
      this.values.temperature = -45;
      this.values.contrast = 25;
      this.values.saturation = 20;
    } else if (preset === 'dramatic-bw') {
      this.values.grayscale = true;
      this.values.contrast = 40;
      this.values.sharpen = 30;
    } else if (preset === 'vintage') {
      this.values.sepia = true;
      this.values.contrast = 15;
      this.values.brightness = 10;
    }

    // Sync UI
    const setVal = (id: string, valId: string, val: number, unit = '') => {
      const s = this.container?.querySelector(`#${id}`) as HTMLInputElement;
      const d = this.container?.querySelector(`#${valId}`) as HTMLElement;
      if (s) s.value = val.toString();
      if (d) d.textContent = `${val}${unit}`;
    };

    setVal('adj-bright', 'val-bright', this.values.brightness);
    setVal('adj-contrast', 'val-contrast', this.values.contrast);
    setVal('adj-exp', 'val-exp', this.values.exposure);
    setVal('adj-sat', 'val-sat', this.values.saturation);
    setVal('adj-temp', 'val-temp', this.values.temperature);
    setVal('adj-blur', 'val-blur', this.values.blur, 'px');
    setVal('adj-sharp', 'val-sharp', this.values.sharpen, '%');

    const chkBw = this.container.querySelector('#chk-bw') as HTMLInputElement;
    if (chkBw) chkBw.checked = this.values.grayscale;
    const chkSepia = this.container.querySelector('#chk-sepia') as HTMLInputElement;
    if (chkSepia) chkSepia.checked = this.values.sepia;
    const chkInvert = this.container.querySelector('#chk-invert') as HTMLInputElement;
    if (chkInvert) chkInvert.checked = this.values.invert;

    this.renderLive();
  }

  private renderLive() {
    const layer = this.editor.getActiveLayer();
    if (!layer || !this.originalSnapshot) return;

    // Restore original first
    layer.ctx.putImageData(this.originalSnapshot, 0, 0);

    // Apply adjustments
    Adjustments.applyAdjustments(layer, this.values);
    this.editor.render();
  }
}
